import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

export type ToastKind = "info" | "error" | "success";

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastApi {
  toast: (message: string, kind?: ToastKind) => void;
}

const Ctx = createContext<ToastApi | null>(null);

/** Komunikaty znikają po 3000 ms — tak jak w prototypie panelu operatora. */
export const TOAST_TTL = 3000;

export function ToastProvider({ children, ttl = TOAST_TTL }: { children: ReactNode; ttl?: number }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const toast = useCallback(
    (message: string, kind: ToastKind = "info") => {
      const id = ++seq.current;
      setItems((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), ttl);
    },
    [ttl],
  );

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="ui-toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={["ui-toast", t.kind !== "info" ? `ui-toast--${t.kind}` : ""].filter(Boolean).join(" ")}>
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useToast musi być użyty wewnątrz <ToastProvider>.");
  return ctx;
}
